import { useCallback, useMemo, useState } from 'react';
import type { AppNotification } from '@/hooks/useNotifications';

/** Slide-out plus collapse; keep in step with the transitions in NotificationRow.module.css. */
export const ROW_EXIT_MS = 380;

const recency = (n: AppNotification) => Date.parse(n.updated_at ?? n.created_at);

/**
 * Animated deletes for a notification list. The delete goes out at once and the row
 * starts its exit immediately; useNotifications drops the row as soon as the server
 * confirms, so the row is held here (in its old place) until the exit has played. If the
 * delete fails the row simply stops exiting and expands back.
 */
export function useRowRemoval(
  list: AppNotification[],
  remove: (id: string) => Promise<{ ok: boolean }>,
) {
  const [exiting, setExiting] = useState<Map<string, AppNotification>>(() => new Map());

  const rows = useMemo(() => {
    if (exiting.size === 0) return list;
    const present = new Set(list.map((n) => n.id));
    const held = [...exiting.values()].filter((n) => !present.has(n.id));
    if (held.length === 0) return list;
    return [...list, ...held].sort((x, y) => recency(y) - recency(x));
  }, [list, exiting]);

  const exitingIds = useMemo(() => new Set(exiting.keys()), [exiting]);

  const release = useCallback((id: string) => {
    setExiting((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const removeAnimated = useCallback(
    async (id: string) => {
      const row = list.find((n) => n.id === id);
      if (!row) return { ok: false };
      setExiting((prev) => new Map(prev).set(id, row));
      const played = new Promise((resolve) => window.setTimeout(resolve, ROW_EXIT_MS));
      const result = await remove(id);
      if (result.ok) await played;
      release(id);
      return result;
    },
    [list, remove, release],
  );

  return { rows, exitingIds, removeAnimated };
}
